import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, Users, Send, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useSanitarioAuth, SANITARIO_OSPITE_KEY } from "../../context/SanitarioAuthContext";

/**
 * Hub comunicazioni operative del modulo rapido.
 * Invio verso medici/parenti: bozza locale + istruzione a usare il gestionale
 * per l'invio formale (nessun canale admin nel tablet).
 */
export default function SanitarioComunicazioni() {
  const { canale } = useParams(); // medici | parenti | undefined
  const { activeOperatore } = useSanitarioAuth();
  const [oggetto, setOggetto] = useState("");
  const [messaggio, setMessaggio] = useState("");
  const [ospiteRef, setOspiteRef] = useState(
    () => localStorage.getItem(SANITARIO_OSPITE_KEY) || ""
  );

  if (!canale) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Comunicazioni</h1>
          <p className="text-sm text-slate-500 mt-1">
            Aggiornamenti rapidi verso medici e parenti, senza accesso amministrativo.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link
            to="/sanitario/comunicazioni/medici"
            className="rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm hover:shadow-md transition"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white mb-3">
              <Stethoscope className="w-6 h-6" />
            </div>
            <h2 className="font-bold text-slate-900">Comunicazione Medici</h2>
            <p className="text-sm text-slate-500 mt-1">
              Aggiornamenti, richieste visita, diario clinico sintetico.
            </p>
          </Link>
          <Link
            to="/sanitario/comunicazioni/parenti"
            className="rounded-2xl border border-amber-200 bg-white p-5 shadow-sm hover:shadow-md transition"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white mb-3">
              <Users className="w-6 h-6" />
            </div>
            <h2 className="font-bold text-slate-900">Comunicazione Parenti</h2>
            <p className="text-sm text-slate-500 mt-1">
              Messaggi filtrati: aggiornamenti, appuntamenti, documenti condividibili.
            </p>
          </Link>
        </div>
      </div>
    );
  }

  const isMedici = canale === "medici";
  const title = isMedici ? "Comunicazione Medici" : "Comunicazione Parenti";
  const hint = isMedici
    ? "Usa un linguaggio clinico sintetico. L'invio formale al medico avviene dal gestionale Comunicazioni."
    : "Solo contenuti autorizzati alla famiglia. Evita dettagli clinici non autorizzati.";

  const handleSaveDraft = (e) => {
    e.preventDefault();
    if (!oggetto.trim() || !messaggio.trim()) {
      toast.error("Compila oggetto e messaggio");
      return;
    }
    const draft = {
      canale,
      oggetto: oggetto.trim(),
      messaggio: messaggio.trim(),
      ospite_id: ospiteRef || null,
      operatore: `${activeOperatore?.nome || ""} ${activeOperatore?.cognome || ""}`.trim(),
      created_at: new Date().toISOString(),
    };
    try {
      const key = "sanitario_comm_drafts";
      const prev = JSON.parse(localStorage.getItem(key) || "[]");
      localStorage.setItem(key, JSON.stringify([draft, ...prev].slice(0, 30)));
      toast.success(
        isMedici
          ? "Bozza salvata sul tablet. Completa l'invio da Comunicazioni nel gestionale."
          : "Bozza salvata. Verifica autorizzazioni prima dell'invio ai parenti dal gestionale."
      );
      setOggetto("");
      setMessaggio("");
    } catch {
      toast.error("Impossibile salvare la bozza");
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <Link
        to="/sanitario/comunicazioni"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft className="w-4 h-4" />
        Tutte le comunicazioni
      </Link>
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500 mt-1">{hint}</p>
      </div>

      <form
        onSubmit={handleSaveDraft}
        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4"
      >
        <div className="space-y-2">
          <Label>Riferimento ospite (ID fascicolo)</Label>
          <Input
            value={ospiteRef}
            onChange={(e) => setOspiteRef(e.target.value)}
            placeholder="Opzionale – ospite in contesto"
          />
        </div>
        <div className="space-y-2">
          <Label>Oggetto</Label>
          <Input
            value={oggetto}
            onChange={(e) => setOggetto(e.target.value)}
            placeholder={isMedici ? "Es. Richiesta visita – Bianchi Maria" : "Es. Aggiornamento settimanale"}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Messaggio</Label>
          <Textarea
            value={messaggio}
            onChange={(e) => setMessaggio(e.target.value)}
            rows={6}
            placeholder={
              isMedici
                ? "Diario clinico sintetico, parametri rilevanti, richiesta…"
                : "Aggiornamento per i familiari (solo info autorizzate)…"
            }
            required
          />
        </div>
        <Button
          type="submit"
          className={`w-full h-11 ${
            isMedici
              ? "bg-emerald-600 hover:bg-emerald-700"
              : "bg-amber-500 hover:bg-amber-600"
          }`}
        >
          <Send className="w-4 h-4 mr-2" />
          Salva bozza operativa
        </Button>
      </form>
    </div>
  );
}
